import type { MarketListing } from "../domain/types.ts";
import { normalizeRoomCount } from "./roomNormalization.ts";
import { publicMarketIdentityNamespace, type PublicMarketQuery, type RawPublicMarketListing } from "./publicMarketTypes.ts";
import {
  canonicalCondition,
  canonicalPropertyType,
  isPlausibleApartmentArea,
  isPlausibleMarketPrice,
  isPlausiblePricePerM2,
  normalizeSlovakLocationName
} from "../domain/propertyMatching.ts";

export const PUBLIC_MARKET_INVALID_REASONS = [
  "missing_municipality",
  "invalid_municipality",
  "missing_property_type",
  "invalid_property_type",
  "missing_rooms",
  "invalid_rooms",
  "missing_area",
  "invalid_area",
  "missing_price",
  "invalid_price",
  "invalid_price_per_m2",
  "invalid_date",
  "impossible_values",
  "parser_failure",
  "other"
] as const;

export type PublicMarketInvalidReason = (typeof PUBLIC_MARKET_INVALID_REASONS)[number];

export const parsePrice = (value: unknown): number | undefined => {
  if (typeof value === "number") return Number.isFinite(value) && value > 0 ? value : undefined;
  if (typeof value !== "string") return undefined;
  const normalized = value
    .replace(/\s|\u00a0/g, "")
    .replace(/€/g, "")
    .replace(/(?<=\d)[.,](?=\d{3}(?:\D|$))/g, "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

export const parseArea = (value: unknown): number | undefined => {
  if (typeof value === "number") return Number.isFinite(value) && value > 0 ? value : undefined;
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().replace(/\u00a0/g, " ");
  const match = normalized.match(/^([0-9]+(?:[.,][0-9]+)?)\s*(?:m\s*(?:2|²))?$/iu);
  if (!match) return undefined;
  const parsed = Number(match[1].replace(",", "."));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const finiteInteger = (value: unknown) => {
  const parsed = parsePrice(value);
  return parsed === undefined ? undefined : Math.round(parsed);
};

const normalizedDate = (value: unknown) =>
  typeof value === "string" && Number.isFinite(Date.parse(value)) ? new Date(value).toISOString() : undefined;

const yesNo = (value: string | boolean | undefined) => {
  if (typeof value === "boolean") return value ? "Áno" : "Nie";
  return value?.trim() ?? "";
};

const propertyType = (value: string | undefined, fallback: string) => {
  const source = value?.trim() || fallback;
  const canonical = canonicalPropertyType(source);
  if (canonical === "apartment") return "Byt";
  if (canonical === "house") return "Rodinný dom";
  if (canonical === "land") return "Pozemok";
  if (canonical === "recreational") return "Rekreačná nehnuteľnosť";
  if (canonical === "commercial") return "Komerčný priestor";
  if (canonical === "garage") return "Garáž";
  if (canonical === "development") return "Developerský projekt";
  return source;
};

const stableHash = (value: string) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};

export const publicMarketInvalidReasons = (
  raw: RawPublicMarketListing,
  query: PublicMarketQuery
): PublicMarketInvalidReason[] => {
  const reasons: PublicMarketInvalidReason[] = [];
  const rawMunicipality = raw.city?.trim() ?? "";
  const normalizedMunicipality = normalizeSlovakLocationName(rawMunicipality);
  const rawPropertyType = raw.propertyType?.trim() ?? "";
  const normalizedPropertyType = propertyType(raw.propertyType, query.propertyType);
  const canonicalType = canonicalPropertyType(normalizedPropertyType);
  const rooms = normalizeRoomCount(raw.rooms) ?? normalizeRoomCount(`${raw.title ?? ""} ${raw.description ?? ""}`);
  const rawArea = raw.areas?.apartmentArea ?? raw.areas?.interiorArea ?? raw.areas?.totalArea ?? raw.area;
  const area = typeof rawArea === "number" ? rawArea : parseArea(rawArea);
  const price = parsePrice(raw.askingPrice);

  if (!rawMunicipality) reasons.push("missing_municipality");
  else if (!normalizedMunicipality || normalizedMunicipality.length < 2 || /^(?:nezname|neuvedene|slovensko)$/.test(normalizedMunicipality)) {
    reasons.push("invalid_municipality");
  }
  if (!rawPropertyType) reasons.push("missing_property_type");
  else if (canonicalType === "other") reasons.push("invalid_property_type");
  if (!raw.sourceUrl.trim()) reasons.push("parser_failure");
  if (raw.capturedAt !== undefined && !normalizedDate(raw.capturedAt)) reasons.push("invalid_date");
  if (!Number.isFinite(Date.parse(query.capturedAt))) reasons.push("invalid_date");

  if (canonicalType === "apartment") {
    if (raw.rooms === undefined || raw.rooms === null || String(raw.rooms).trim() === "") {
      if (rooms === undefined) reasons.push("missing_rooms");
    } else if (rooms === undefined) reasons.push("invalid_rooms");
    if (rawArea === undefined || rawArea === null || String(rawArea).trim() === "") reasons.push("missing_area");
    else if (area === undefined) reasons.push("invalid_area");
    else if (!isPlausibleApartmentArea(area)) reasons.push("impossible_values");
    if (raw.askingPrice === undefined || raw.askingPrice === null || String(raw.askingPrice).trim() === "") reasons.push("missing_price");
    else if (price === undefined) reasons.push("invalid_price");
    else if (!isPlausibleMarketPrice(price)) reasons.push("impossible_values");
    if (area !== undefined && price !== undefined && !isPlausiblePricePerM2(price, area)) reasons.push("invalid_price_per_m2");
  }
  return [...new Set(reasons)];
};

export const normalizePublicMarketListing = (
  raw: RawPublicMarketListing,
  query: PublicMarketQuery
): MarketListing | null => {
  const rooms = normalizeRoomCount(raw.rooms) ?? normalizeRoomCount(`${raw.title ?? ""} ${raw.description ?? ""}`);
  const city = raw.city?.trim() || query.city;
  const normalizedPropertyType = propertyType(raw.propertyType, query.propertyType);
  if (publicMarketInvalidReasons(raw, query).length > 0) return null;

  const area = parseArea(raw.area);
  const askingPrice = parsePrice(raw.askingPrice);
  const capturedAt = normalizedDate(raw.capturedAt) ?? query.capturedAt;
  const externalKey = raw.externalId?.trim() || raw.sourceUrl.trim();
  const id = `${raw.providerId}:${stableHash(externalKey)}`;
  const canonicalType = canonicalPropertyType(normalizedPropertyType);
  const effectiveArea = raw.areas?.apartmentArea ?? raw.areas?.interiorArea ?? raw.areas?.totalArea ?? area;
  const location = raw.location ?? {
    municipality: city,
    municipalityNormalized: normalizeSlovakLocationName(city),
    municipalityId: raw.municipalityId,
    locality: raw.locality?.trim(),
    street: raw.street?.trim()
  };
  const condition = raw.condition?.trim();

  return {
    id,
    propertyId: id,
    kind: "active",
    askingPrice,
    listedAt: normalizedDate(raw.publishedAt),
    capturedAt,
    status: raw.status?.trim() || "Aktívna ponuka",
    sourceName: raw.sourceName.trim(),
    sourceUrl: raw.sourceUrl.trim(),
    externalId: raw.externalId?.trim(),
    title: raw.title?.trim(),
    city,
    street: raw.street?.trim(),
    locality: raw.locality?.trim(),
    propertyType: normalizedPropertyType,
    rooms,
    area,
    pricePerM2: parsePrice(raw.pricePerM2) ?? (askingPrice && effectiveArea ? askingPrice / effectiveArea : undefined),
    condition,
    electricity: raw.electricity?.trim(),
    coreType: raw.coreType?.trim(),
    kitchen: raw.kitchen?.trim(),
    floor: finiteInteger(raw.floor),
    totalFloors: finiteInteger(raw.totalFloors),
    elevator: yesNo(raw.elevator),
    balconyType: raw.balconyType?.trim(),
    cellar: yesNo(raw.cellar),
    orientation: raw.orientation?.trim(),
    buildingType: raw.buildingType?.trim(),
    reconstruction: raw.reconstruction?.trim(),
    description: raw.description?.trim(),
    agency: raw.agency?.trim(),
    municipalityId: raw.municipalityId ?? location.municipalityId,
    location,
    areas: raw.areas ?? (area ? { totalArea: area, areaBasis: "total_only" } : undefined),
    technical: raw.technical ?? { condition: canonicalCondition(condition) },
    equipment: raw.equipment,
    canonicalPropertyType: canonicalType,
    fieldProvenance: raw.fieldProvenance,
    sources: [{
      providerId: raw.providerId,
      sourceName: raw.sourceName.trim(),
      sourceUrl: raw.sourceUrl.trim(),
      externalId: raw.externalId?.trim(),
      identityNamespace: publicMarketIdentityNamespace(raw.providerId),
      capturedAt
    }]
  };
};
